import { useEffect, useMemo, useState } from "react";
import { ShieldIcon } from "./Icons";
import { addAdmin, sendBroadcast, listDomains, setDomain, setUserCategory } from "./broadcastNotify";
import type { AdminInfo, DomainRow, UserRow, DomainCategory } from "./broadcastNotify";
import AdminOrganise from "./AdminOrganise";

const MAX_LEN = 300;

type AdminTab = "organise" | "access" | "announce" | "admins";

const TABS: { key: AdminTab; label: string }[] = [
  { key: "organise", label: "Organising" },
  { key: "access", label: "Access control" },
  { key: "announce", label: "Announcement" },
  { key: "admins", label: "Admins" },
];

/** Admin-only, full-screen control panel: decide who can discover whom
 * (by email domain), broadcast an announcement to everyone, and grant
 * admin access to other emails. Entry point (DBMWorkspace.tsx) only
 * renders this once dbm-admin-status has confirmed the caller is an
 * admin; every write action here is independently re-verified
 * server-side regardless. */
export default function AdminPanel({
  admins: initialAdmins,
  myEmail,
  onClose,
}: {
  admins: AdminInfo[];
  myEmail?: string;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<AdminTab>("organise");

  const [admins, setAdmins] = useState(initialAdmins);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sentNote, setSentNote] = useState("");
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [addError, setAddError] = useState("");

  // Email domains, and what each one is. Domains appear here on their own
  // the first time anyone from them signs in, and an admin can also add
  // one by hand — either way, categorising is a single click per domain.
  const [domains, setDomains] = useState<DomainRow[] | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [enforced, setEnforced] = useState(false);
  const [busyDomain, setBusyDomain] = useState<string | null>(null);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);
  const [domainError, setDomainError] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [domainQuery, setDomainQuery] = useState("");

  useEffect(() => {
    listDomains()
      .then((d) => {
        setDomains(d.domains);
        setUsers(d.users);
        setEnforced(d.enforced);
      })
      .catch((e) => {
        console.warn("domain list failed", e);
        setDomains([]);
      });
  }, []);

  const applyDomain = async (
    domain: string,
    fields: { category?: DomainCategory | null; company?: string | null; visibleToExternal?: boolean }
  ) => {
    setBusyDomain(domain);
    setDomainError("");
    try {
      const d = await setDomain(domain, fields);
      setDomains(d.domains);
      setUsers(d.users);
      setEnforced(d.enforced);
    } catch (e) {
      setDomainError(e instanceof Error ? e.message : "Couldn't save that");
    } finally {
      setBusyDomain(null);
    }
  };

  /* Category moves from the board apply locally first, then reconcile with
   * the server's echo. A drag that only lands once the round trip finishes
   * reads as a broken drop, so the card moves immediately and rolls back
   * (with the error shown) if the save actually fails. */
  const moveDomain = async (domain: string, category: DomainCategory | null) => {
    const before = domains;
    setDomains((prev) =>
      prev ? prev.map((d) => (d.domain === domain ? { ...d, category } : d)) : prev
    );
    if (category) setEnforced(true);
    setDomainError("");
    try {
      const d = await setDomain(domain, { category });
      setDomains(d.domains);
      setUsers(d.users);
      setEnforced(d.enforced);
    } catch (e) {
      setDomains(before);
      setEnforced(!!before?.some((d) => d.category));
      setDomainError(e instanceof Error ? e.message : "Couldn't move that domain");
    }
  };

  /* Same optimistic treatment as domain moves. A person's effective
   * category is their override falling back to their domain's, so
   * clearing one (category === null) has to recompute from domainCategory
   * rather than just blanking the field. */
  const moveUser = async (email: string, category: DomainCategory | null) => {
    const before = users;
    setBusyEmail(email);
    setUsers((prev) =>
      prev.map((u) =>
        u.email === email
          ? { ...u, override: category, category: category || u.domainCategory }
          : u
      )
    );
    setDomainError("");
    try {
      const d = await setUserCategory(email, category);
      setDomains(d.domains);
      setUsers(d.users);
      setEnforced(d.enforced);
    } catch (e) {
      setUsers(before);
      setDomainError(e instanceof Error ? e.message : "Couldn't move that person");
    } finally {
      setBusyEmail(null);
    }
  };

  // Pre-classify a domain before anyone from it has ever signed in — no
  // need to wait on a real login when you already know it belongs to a
  // company.
  const addDomainManually = async () => {
    const domain = newDomain.trim().toLowerCase().replace(/^@/, "");
    if (!domain) return;
    setBusyDomain(domain);
    setDomainError("");
    try {
      const d = await setDomain(domain, {});
      setDomains(d.domains);
      setEnforced(d.enforced);
      setNewDomain("");
    } catch (e) {
      setDomainError(e instanceof Error ? e.message : "Couldn't add that domain");
    } finally {
      setBusyDomain(null);
    }
  };

  // A search box keeps the list usable once there are lots of domains —
  // type any part of a domain or company name to jump straight to it
  // instead of scrolling.
  const visibleDomains = useMemo(() => {
    const q = domainQuery.trim().toLowerCase();
    if (!q) return domains || [];
    return (domains || []).filter(
      (d) => d.domain.includes(q) || (d.company || "").toLowerCase().includes(q)
    );
  }, [domains, domainQuery]);

  const pending = visibleDomains.filter((d) => !d.category);
  const classified = visibleDomains.filter((d) => d.category);
  const totalPending = (domains || []).filter((d) => !d.category).length;

  const broadcast = async () => {
    const text = message.trim();
    if (!text) return;
    setSending(true);
    setSentNote("");
    try {
      await sendBroadcast(text);
      setMessage("");
      setSentNote("Sent to everyone ✓");
    } catch (e) {
      setSentNote(e instanceof Error ? e.message : "Couldn't send — try again");
    } finally {
      setSending(false);
    }
  };

  const addNewAdmin = async () => {
    const email = newAdminEmail.trim();
    if (!email) return;
    setAddingAdmin(true);
    setAddError("");
    try {
      const updated = await addAdmin(email);
      setAdmins(updated);
      setNewAdminEmail("");
    } catch (e) {
      setAddError(e instanceof Error ? e.message : "Couldn't add that admin");
    } finally {
      setAddingAdmin(false);
    }
  };

  return (
    <div className="admin-fs-backdrop" onClick={onClose}>
      <div
        className={`admin-fs ${tab === "organise" ? "admin-fs--wide" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="admin-fs__head">
          <b className="admin-fs__brand">
            <ShieldIcon size={16} />
            Admin
          </b>
          <nav className="admin-fs__tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`admin-fs__tab ${tab === t.key ? "on" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {t.key === "organise" && totalPending > 0 && (
                  <span className="admin-fs__tabdot">{totalPending}</span>
                )}
              </button>
            ))}
          </nav>
          <button className="admin-fs__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        <div className="admin-fs__body">
          {tab === "organise" && (
            <div className="admin-fs__col admin-fs__col--wide">
              <AdminOrganise
                domains={domains}
                users={users}
                enforced={enforced}
                busyDomain={busyDomain}
                busyEmail={busyEmail}
                error={domainError}
                query={domainQuery}
                onQueryChange={setDomainQuery}
                newDomain={newDomain}
                onNewDomainChange={setNewDomain}
                onAddDomain={() => void addDomainManually()}
                onMove={(domain, category) => void moveDomain(domain, category)}
                onSetCompany={(domain, company) => applyDomain(domain, { company })}
                onSetVisibleToExternal={(domain, visible) =>
                  applyDomain(domain, { visibleToExternal: visible })
                }
                onMoveUser={(email, category) => void moveUser(email, category)}
              />
            </div>
          )}

          {tab === "access" && (
            <div className="admin-fs__col">
              <div className="admin-fs__titlerow">
                <div>
                  <h2 className="admin-fs__h2">Who can see whom</h2>
                  <p className="admin-fs__sub">
                    A domain's category decides who its people can find in search and add
                    to chats. <b>Team</b> sees everyone; suppliers, customers and partners
                    see only their own company plus any Team domain marked visible to
                    externals.
                  </p>
                </div>
                <span className={`admin-fs__state ${enforced ? "on" : ""}`}>
                  {enforced ? "Enforcing" : "Open — nothing restricted yet"}
                </span>
              </div>

              {domains === null ? (
                <div className="admin-fs__empty">Loading…</div>
              ) : (
                <>
                  <div className="admin-fs__tools">
                    <div className="admin-fs__addrow">
                      <input
                        type="text"
                        placeholder="Add a domain, e.g. example.com.au"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void addDomainManually();
                        }}
                      />
                      <button
                        className="admin-btn"
                        disabled={!newDomain.trim() || busyDomain !== null}
                        onClick={() => void addDomainManually()}
                      >
                        {busyDomain === newDomain.trim().toLowerCase().replace(/^@/, "")
                          ? "Adding…"
                          : "Add"}
                      </button>
                    </div>
                    <input
                      className="admin-fs__search"
                      type="text"
                      placeholder="Search domains…"
                      value={domainQuery}
                      onChange={(e) => setDomainQuery(e.target.value)}
                    />
                  </div>

                  {domainError && (
                    <div className="admin-note admin-note--error">{domainError}</div>
                  )}

                  {domains.length === 0 && (
                    <div className="admin-fs__empty">
                      No domains yet — add one above, or they'll appear here as people sign in.
                    </div>
                  )}

                  {domains.length > 0 && visibleDomains.length === 0 && (
                    <div className="admin-fs__empty">No domains match “{domainQuery.trim()}”.</div>
                  )}

                  {pending.length > 0 && (
                    <section className="admin-fs__group">
                      <div className="admin-fs__grouphead admin-fs__grouphead--pending">
                        Needs categorising · {pending.length}
                      </div>
                      {pending.map((d) => (
                        <DomainRowView
                          key={d.domain}
                          row={d}
                          busy={busyDomain === d.domain}
                          onSet={applyDomain}
                        />
                      ))}
                    </section>
                  )}

                  {classified.length > 0 && (
                    <section className="admin-fs__group">
                      <div className="admin-fs__grouphead">Categorised · {classified.length}</div>
                      {classified.map((d) => (
                        <DomainRowView
                          key={d.domain}
                          row={d}
                          busy={busyDomain === d.domain}
                          onSet={applyDomain}
                        />
                      ))}
                    </section>
                  )}
                </>
              )}
            </div>
          )}

          {tab === "announce" && (
            <div className="admin-fs__col admin-fs__col--narrow">
              <h2 className="admin-fs__h2">Send an announcement</h2>
              <p className="admin-fs__sub">
                Every person using dbm. chat sees this in their notification bell. Use it
                sparingly — it reaches everyone at once.
              </p>
              <textarea
                className="admin-textarea"
                value={message}
                maxLength={MAX_LEN}
                placeholder="Everyone using dbm. chat will see this…"
                onChange={(e) => setMessage(e.target.value)}
              />
              <div className="admin-composer-foot">
                <span className="admin-charcount">
                  {message.length}/{MAX_LEN}
                </span>
                <button
                  className="admin-btn"
                  disabled={!message.trim() || sending}
                  onClick={() => void broadcast()}
                >
                  {sending ? "Sending…" : "Broadcast to everyone"}
                </button>
              </div>
              {sentNote && <div className="admin-note">{sentNote}</div>}
            </div>
          )}

          {tab === "admins" && (
            <div className="admin-fs__col admin-fs__col--narrow">
              <h2 className="admin-fs__h2">Admins</h2>
              <p className="admin-fs__sub">
                Admins can open this panel, categorise domains, and broadcast
                announcements.
              </p>
              <div className="admin-fs__admins">
                {admins.map((a) => (
                  <div className="admin-fs__adminrow" key={a.email}>
                    <span className="nm">{a.email}</span>
                    {a.email === myEmail && <span className="members-row__you">you</span>}
                  </div>
                ))}
              </div>
              <div className="admin-fs__addrow">
                <input
                  type="email"
                  placeholder="name@company.com"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void addNewAdmin();
                  }}
                />
                <button
                  className="admin-btn"
                  disabled={!newAdminEmail.trim() || addingAdmin}
                  onClick={() => void addNewAdmin()}
                >
                  {addingAdmin ? "Adding…" : "Add"}
                </button>
              </div>
              {addError && <div className="admin-note admin-note--error">{addError}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CATEGORY_LABELS: { key: DomainCategory; label: string }[] = [
  { key: "team", label: "Team" },
  { key: "supplier", label: "Supplier" },
  { key: "customer", label: "Customer" },
  { key: "partner", label: "Partner" },
];

/** One email domain: pick its category with a single click, clear it with
 * the ×, optionally name the company (which links multiple domains of the
 * same business into one visible group), and — for Team domains only —
 * say whether external users can see it. That last toggle is what makes
 * DBM reachable by suppliers/customers while keeping the other internal
 * companies hidden from them. */
function DomainRowView({
  row,
  busy,
  onSet,
}: {
  row: DomainRow;
  busy: boolean;
  onSet: (
    domain: string,
    fields: { category?: DomainCategory | null; company?: string | null; visibleToExternal?: boolean }
  ) => void;
}) {
  const [company, setCompany] = useState(row.company || "");

  // Keep in step when the server echoes back a changed row (e.g. another
  // admin renamed the company while this panel was open).
  useEffect(() => {
    setCompany(row.company || "");
  }, [row.company]);

  return (
    <div className={`adom ${busy ? "is-busy" : ""}`}>
      <div className="adom__main">
        <div className="adom__id">
          <span className="adom__name">@{row.domain}</span>
          <span className="adom__count">
            {row.people} {row.people === 1 ? "person" : "people"}
            {/* Anyone who hasn't signed in yet has no chat account, so
                they can't appear in the sidebar directory — saying so
                here stops the two numbers looking like a bug. */}
            {row.active < row.people && ` · ${row.active} on chat`}
          </span>
        </div>
        <div className="adom__cats">
          {CATEGORY_LABELS.map((c) => (
            <button
              key={c.key}
              className={`adom__cat ${row.category === c.key ? "on" : ""}`}
              disabled={busy}
              onClick={() =>
                onSet(row.domain, { category: row.category === c.key ? null : c.key })
              }
            >
              {c.label}
            </button>
          ))}
          {row.category && (
            <button
              className="adom__clear"
              title="Remove category"
              aria-label={`Remove category from ${row.domain}`}
              disabled={busy}
              onClick={() => onSet(row.domain, { category: null })}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {row.category && (
        <div className="adom__extras">
          <input
            className="adom__company"
            placeholder="Company name (optional)"
            value={company}
            disabled={busy}
            onChange={(e) => setCompany(e.target.value)}
            onBlur={() => {
              if ((row.company || "") !== company.trim()) {
                onSet(row.domain, { company: company.trim() || null });
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
          {row.category === "team" && (
            <label className="adom__toggle">
              <input
                type="checkbox"
                checked={row.visible_to_external}
                disabled={busy}
                onChange={(e) => onSet(row.domain, { visibleToExternal: e.target.checked })}
              />
              <span>Visible to external companies</span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}
